import type { WorkflowInstanceView, WorkflowNode } from "@ncp/api-client";

/** The phases a case moves through, in order. Not every workflow has every phase. */
export type PhaseKey = "intake" | "review" | "publication" | "assessment" | "decision" | "closure";
export const PHASE_ORDER: PhaseKey[] = ["intake", "review", "publication", "assessment", "decision", "closure"];
/** A concentration is published for objections as soon as it is registered, so its publication comes before the review. */
export const EC_PHASE_ORDER: PhaseKey[] = ["intake", "publication", "review", "assessment", "decision", "closure"];

/**
 * Where a stage stands for this case: already passed, the one the case is in, still reachable from here, or no longer on the
 * path (a branch the case did not take – the simplified track once the substantive review has begun, the review phases once the
 * Under-Secretary has found the concentration not notifiable, …).
 */
export type StageStanding = "visited" | "current" | "ahead" | "bypassed";
export type PhaseStanding = "done" | "current" | "ahead" | "skipped";

export interface Stage { node: WorkflowNode; standing: StageStanding }
export interface Phase { key: PhaseKey; stages: Stage[]; standing: PhaseStanding }

/** The concentration workflow grouped as in docs/ec-review-process.md §1. States the map does not know fall back to their case status. */
const EC_PHASES: Partial<Record<string, PhaseKey>> = {
  Draft: "intake", PreNotification: "intake", Screening: "intake", Received: "intake", FormalReview: "intake", AwaitingCompletion: "intake", Registered: "intake",
  SimplifiedReview: "review", SubstantiveReview: "review", PartyMeetings: "review",
  Publication: "publication", ObjectionsReview: "publication",
  Assessment: "assessment", PreliminaryFindings: "assessment", RemedyDesign: "assessment",
  CommitteeOpinion: "decision", UndersecretaryDecision: "decision", Escalation: "decision", MinisterDecision: "decision",
  Decided: "closure", Closed: "closure", Withdrawn: "closure", Returned: "closure",
};

/** Every other workflow: the phase follows from the case status the state carries. */
const STATUS_PHASES: Partial<Record<string, PhaseKey>> = {
  Draft: "intake", Submitted: "intake",
  UnderReview: "review", AwaitingApplicant: "review", Accepted: "review",
  Published: "publication",
  Decision: "decision",
  Decided: "closure", Closed: "closure", Rejected: "closure", Withdrawn: "closure", Suspended: "closure",
};

function phaseOf(node: WorkflowNode, caseType: string): PhaseKey {
  const mapped = caseType === "EconomicConcentration" ? EC_PHASES[node.key] : undefined;
  return mapped ?? STATUS_PHASES[node.status ?? ""] ?? (node.kind === "End" ? "closure" : "review");
}

/**
 * The stage of a file from its state (and status), outside the case screen: lists, the home page and statistics name a file's stage with
 * the same six words as its stage bar (7.5), never with a status or a step label of their own.
 */
export function stageOf(caseType: string, state: string, status?: string | null): PhaseKey {
  const mapped = caseType === "EconomicConcentration" ? EC_PHASES[state] : undefined;
  return mapped ?? STATUS_PHASES[status ?? ""] ?? (["Closed", "Decided", "Withdrawn", "Rejected"].includes(state) ? "closure" : "review");
}

/** The states the case can still reach from where it is, following the definition's edges (the current state included). */
export function reachableStates(workflow: WorkflowInstanceView): Set<string> {
  const seen = new Set<string>([workflow.currentState]);
  const queue = [workflow.currentState];
  while (queue.length > 0) {
    const from = queue.shift()!;
    for (const e of workflow.definition.edges) {
      if (e.from !== from || seen.has(e.to)) continue;
      seen.add(e.to);
      queue.push(e.to);
    }
  }
  return seen;
}

/** The section of the Department that owns a case type (mirrors WorkflowSections.ForCaseType on the server). */
export function sectionForCaseType(caseType: string): string {
  if (caseType === "EconomicConcentration") return "EconomicConcentration";
  if (caseType === "Complaint" || caseType === "Whistleblowing") return "Investigations";
  if (caseType === "MarketStudy") return "Advocacy";
  return "Requests";
}

/** Nodes in the order the definition draws them. */
function orderedNodes(workflow: WorkflowInstanceView): WorkflowNode[] {
  return workflow.definition.nodes
    .map((n, i) => ({ n, i }))
    .sort((a, b) => (a.n.order ?? a.i) - (b.n.order ?? b.i))
    .map((x) => x.n);
}

/**
 * The path of this case as phases of stages, each stage marked with where it stands. Only the stages that apply to the case are on it:
 * those it has passed through, the one it is in, and those it can still reach that belong to its own section or to the Department's
 * leadership. Branches it did not take and another section's stages stay in the full map of the procedure guide; another section's work
 * is referred to it as a linked file. Alternative endings only appear once the case has actually taken them; the regular closure always does.
 */
export function processPhases(workflow: WorkflowInstanceView): Phase[] {
  const reachable = reachableStates(workflow);
  // The journal records the states a case moved *into*; the state it was born in is visited by definition.
  const visited = new Set([workflow.definition.initialState, ...workflow.visitedStates]);
  const nodes = orderedNodes(workflow);
  const regularEnd = nodes.find((n) => n.kind === "End" && n.status === "Closed") ?? nodes.find((n) => n.kind === "End");
  const stagesByPhase = new Map<PhaseKey, Stage[]>();
  const ownSection = sectionForCaseType(workflow.definition.caseType);
  const closed = nodes.find((n) => n.key === workflow.currentState)?.kind === "End";

  for (const node of nodes) {
    const standing: StageStanding = node.key === workflow.currentState ? "current" : visited.has(node.key) ? "visited" : reachable.has(node.key) ? "ahead" : "bypassed";
    if (node.kind === "End" && node.key !== regularEnd?.key && standing !== "current" && standing !== "visited") continue;
    if (standing === "bypassed") continue;
    // A closed case shows the path it actually took; stages it never reached are not outstanding work.
    if (closed && standing === "ahead") continue;
    if (standing === "ahead" && node.section && node.section !== ownSection && node.section !== "Leadership") continue;
    const key = phaseOf(node, workflow.definition.caseType);
    const list = stagesByPhase.get(key) ?? [];
    list.push({ node, standing });
    stagesByPhase.set(key, list);
  }

  const order = workflow.definition.caseType === "EconomicConcentration" ? EC_PHASE_ORDER : PHASE_ORDER;
  const present = order.filter((k) => (stagesByPhase.get(k)?.length ?? 0) > 0);
  const currentIndex = present.findIndex((k) => stagesByPhase.get(k)!.some((s) => s.standing === "current"));
  return present.map((key, i) => {
    const stages = stagesByPhase.get(key)!;
    let standing: PhaseStanding;
    if (i === currentIndex) standing = "current";
    else if (currentIndex >= 0 && i < currentIndex) standing = stages.some((s) => s.standing === "visited") ? "done" : "skipped";
    else standing = stages.some((s) => s.standing === "ahead") ? "ahead" : stages.some((s) => s.standing === "visited") ? "done" : "skipped";
    return { key, stages, standing };
  });
}

/** Everything the current stage can lead to, in the definition's order, with the workflow's own view of whether each move is open. */
export interface StageExit { trigger: string; to: WorkflowNode | undefined; label: string; labelAr?: string | null; automatic: boolean; guard?: string | null; guardSatisfied: boolean | null; permitted: boolean }

export function stageExits(workflow: WorkflowInstanceView): StageExit[] {
  return workflow.definition.edges
    .filter((e) => e.from === workflow.currentState)
    .map((e) => {
      const available = workflow.availableTransitions.find((tr) => tr.trigger === e.trigger);
      return {
        trigger: e.trigger,
        to: workflow.definition.nodes.find((n) => n.key === e.to),
        label: e.label ?? available?.label ?? e.trigger,
        labelAr: e.labelAr ?? available?.labelAr,
        automatic: e.automatic ?? available?.automatic ?? false,
        guard: e.guard ?? available?.guard,
        guardSatisfied: available ? available.guardSatisfied : null,
        permitted: available?.permittedForCurrentUser ?? false,
      };
    });
}
